import { map, debounce, isEmpty, pluck } from 'underscore';
import { useCallback, useMemo, useContext, useRef, useEffect, useState } from 'react';

import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { getAdCreatorInstrumentation, instrumentationScenarios } from '@bingads-webui-campaign/ad-creator-tools-instrumentation';
import { SuggestionPoller, SuggestionStatus, mapSuggestionResponseToState, hasMoreImagesToFetch } from '@bingads-webui-campaign/media-repository';
import { constants } from '@bingads-webui-campaign-react/image-experience-model';

import { getODataConfig } from './utils';
import { RecommendationContext } from './recommendation-context';

export function useImageRecommendations({
  cid,
  aid,
  campaignId,
  permissions,
  instrumentation,
  enableCopilotRecommendations = true,
}) {
  const serverContext = useContext(ServerContext);
  const [recommendationContext, setRecommendationContext, reportRecommendations] = useContext(RecommendationContext);

  const oDataConfig = useMemo(() => getODataConfig(serverContext), [serverContext]);

  // states keeping online/offline call status and responses
  const [onlineSuggestionStatus, setOnlineSuggestionStatus] = useState(SuggestionStatus.FetchSucceed);
  const [onlineRecommendationResponse, setOnlineRecommendationResponse] = useState(null);
  const [offlineRecommendationResponse, setOfflineRecommendationResponse] = useState(null);

  // states keeping current state of each component:
  // websiteImagesState - holds the state provided to image-experience to manage asset-library website images
  // carouselState - holds the state managing suggestion carousel. Can show online or offline recommendations
  // panelSate - hold the state managing recommended images panel. This pannel is accessible only from the carousel.
  const [websiteImagesState, setWebsiteImagesState] = useState(null);
  const [carouselState, setCarouselState] = useState(null);
  const [panelState, setPanelState] = useState(null);

  // refs keeping state needed for online recommendations pagination
  const pageNumber = useRef(1);
  const onlineHasMore = useRef(false);
  const currentFinalUrl = useRef(null);
  const getMoreImageSuggestionsRef = useRef(null);

  const minimumSize = useMemo(() => constants.getMinimumSizeForImage(), []);

  const onPollingSuggestionCompleted = useCallback((response, isSubdomainScraping) => {
    const suggestionStatus = response.error ? SuggestionStatus.ServerError : SuggestionStatus.FetchSucceed;
    setOnlineSuggestionStatus(suggestionStatus);
    setOnlineRecommendationResponse(response);
    setWebsiteImagesState(mapSuggestionResponseToState(suggestionStatus, response, isSubdomainScraping));
  }, []);

  const suggestionPoller = useMemo(() => new SuggestionPoller({
    cid,
    aid,
    cmpId: campaignId,
    oDataConfig,
    jobCompleteCallback: onPollingSuggestionCompleted,
    logger: getAdCreatorInstrumentation(instrumentation, instrumentationScenarios.MSAN, permissions).websiteSuggestionActivity, // eslint-disable-line max-len
  }), [aid, cid, campaignId, instrumentation, oDataConfig, onPollingSuggestionCompleted, permissions]);

  const getPanelImagesFromResponse = useCallback(response => map(response && response.Images, ({ ImageUrl, ThumbnailUrl, SuggestionId }) => ({
    fullUrl: ImageUrl,
    thumbnailSrc: ImageUrl || ThumbnailUrl,
    thumbnailUrl: ImageUrl || ThumbnailUrl,
    suggestionId: SuggestionId,
  })), []);

  // This is called by recommended image panel when showing online recommendations. Website images panel will call suggestionPoller directly
  // to grab additional images if needed.
  const getMoreImageSuggestions = useCallback(() => {
    if (onlineHasMore.current) {
      pageNumber.current += 1;
      suggestionPoller.getMoreLandingPageSuggestions({
        landingPageUrl: currentFinalUrl.current,
        pageNumber: pageNumber.current,
        minimumResolution: {
          width: minimumSize[0],
          height: minimumSize[1],
        },
      }).then((response) => {
        setPanelState({
          panelImages: [...panelState.panelImages, ...getPanelImagesFromResponse(response)],
          hasMore: hasMoreImagesToFetch(response),
          getMoreImageSuggestions: getMoreImageSuggestionsRef.current,
        });
      });
    }
  }, [getPanelImagesFromResponse, minimumSize, suggestionPoller, panelState]);

  // Regardless if online/offline recommendation, this should always be triggerd on URL change for website images.
  // We migtht use the response of the request initiated here if we wont have offline recommendations for this url.
  const getImageSuggestions = useCallback(debounce((url) => {
    suggestionPoller.cancel();
    setOnlineSuggestionStatus(SuggestionStatus.FetchInProgress);
    setWebsiteImagesState(mapSuggestionResponseToState(SuggestionStatus.FetchInProgress));
    currentFinalUrl.current = url;
    pageNumber.current = 1;
    onlineHasMore.current = false;
    suggestionPoller.getLandingPageSuggestions({
      landingPageUrl: url,
      enableOfflinePollingDelay: true,
      minimumResolution: {
        width: minimumSize[0],
        height: minimumSize[1],
      },
    });
  }, 2000), [suggestionPoller, minimumSize]);

  const cancelGetImageSuggestions = useCallback(() => {
    getImageSuggestions.cancel();
    suggestionPoller.cancel();
    setOnlineSuggestionStatus(SuggestionStatus.FetchSucceed);
    setOnlineRecommendationResponse(null);
    setWebsiteImagesState(mapSuggestionResponseToState(SuggestionStatus.FetchSucceed));
  }, [getImageSuggestions, suggestionPoller]);

  // Main starting point for image recommendation:
  // Initialize all states and cached responses. Trigger online request for website images.
  const initImageRecommendationForUrl = useCallback((url) => {
    cancelGetImageSuggestions();
    setOnlineSuggestionStatus(SuggestionStatus.FetchSucceed);
    setOnlineRecommendationResponse(null);
    setOfflineRecommendationResponse(null);
    setWebsiteImagesState(mapSuggestionResponseToState(SuggestionStatus.FetchSucceed));
    setCarouselState(null);
    setPanelState(null);
    if (!isEmpty(url)) {
      getImageSuggestions(url);
    }
  }, [cancelGetImageSuggestions, getImageSuggestions]);

  getMoreImageSuggestionsRef.current = getMoreImageSuggestions;

  const setOfflineImageRecommendations = useCallback((offlineAssetsSuggestions) => {
    setOfflineRecommendationResponse(offlineAssetsSuggestions);
  }, []);

  const updateImageRecommendations = useCallback(() => {
    if (enableCopilotRecommendations) {
      // We only pick website images here if copilot recommendations is enabled, and
      // images for carousel and panel are picked from copilot recommendations response.
      setRecommendationContext({
        images: {
          websiteImagesState,
        },
      });
    } else {
      const returnedImages = (panelState && panelState.panelImages) || [];
      reportRecommendations({ returned: pluck(returnedImages, 'suggestionId') });

      setRecommendationContext({
        images: {
          websiteImagesState,
          carouselState,
          panelState,
        },
      });
    }
  }, [enableCopilotRecommendations, setRecommendationContext, reportRecommendations, websiteImagesState, carouselState, panelState]);

  // Update carousel and panel state by online or offline recommendation update
  useEffect(() => {
    if (!enableCopilotRecommendations) {
      if (recommendationContext.recommendationSource === 'undetermined') {
        // Undetermined means we are waiting for offline recommendation to be fetched or recommendation phase2 pilot is off. set SuggestionStatus.FetchSucceed to show nothing.
        setCarouselState(mapSuggestionResponseToState(SuggestionStatus.FetchSucceed));
      } else if (recommendationContext.recommendationSource === 'offline' && offlineRecommendationResponse) {
        setCarouselState(mapSuggestionResponseToState(SuggestionStatus.FetchSucceed, offlineRecommendationResponse));
        setPanelState({
          panelImages: getPanelImagesFromResponse(offlineRecommendationResponse),
          hasMore: false,
          getMoreImageSuggestions: () => {},
        });
      } else if (recommendationContext.recommendationSource === 'online') {
        setCarouselState(mapSuggestionResponseToState(onlineSuggestionStatus, onlineRecommendationResponse));
        // if page number > 1 then online response is subsequent pages and state has been update by getMoreImageSuggestions
        if (pageNumber.current === 1) {
          setPanelState({
            panelImages: getPanelImagesFromResponse(onlineRecommendationResponse),
            hasMore: onlineHasMore.current,
            getMoreImageSuggestions: getMoreImageSuggestionsRef.current,
          });
        }
      }
    }
  }, [enableCopilotRecommendations, offlineRecommendationResponse, onlineRecommendationResponse, recommendationContext.recommendationSource, onlineSuggestionStatus, getPanelImagesFromResponse]);

  // Update recommendation context when websiteImages, carousel or panel state changes
  useEffect(() => {
    updateImageRecommendations();
  }, [websiteImagesState, carouselState, panelState, updateImageRecommendations]);

  return {
    suggestionPoller,
    initImageRecommendationForUrl,
    setOfflineImageRecommendations,
  };
}
